import { useState, useCallback, useMemo } from 'react'
import * as tus from 'tus-js-client'

const ALLOWED_FILE_TYPES = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp', '.eml', '.msg']
const MAX_FILE_SIZE = 1 * 1024 * 1024 * 1024 // 1 GB

export interface UploadFile {
  id: string
  file: File
  name: string
  size: number
  type: string
  progress: number
  status: 'pending' | 'uploading' | 'success' | 'error'
  uploadedData?: string
  error?: string
  upload?: tus.Upload
  bytesUploaded?: number
  lastUpdateTime?: number
  transferRate?: number // bytes per second
}

export function useTusUpload() {
  const [files, setFiles] = useState<Map<string, UploadFile>>(new Map())

  // Validate file
  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 1 GB limit`
    }

    // Check file extension
    const extension = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_FILE_TYPES.includes(extension)) {
      return `File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(', ')}`
    }

    return null
  }

  // Add files (will be defined properly after uploadFile)
  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const filesArray = Array.from(newFiles)
    const fileIds: string[] = []

    filesArray.forEach((file) => {
      const error = validateFile(file)
      const id = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`

      setFiles((prev) => {
        const next = new Map(prev)
        next.set(id, {
          id,
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          progress: 0,
          status: error ? 'error' : 'pending',
          error: error || undefined,
        })
        return next
      })

      if (!error) {
        fileIds.push(id)
      }
    })

    // Upload all new files after state updates
    fileIds.forEach((id) => {
      // Use setTimeout to ensure state has updated
      setTimeout(() => uploadFileById(id), 0)
    })
  }, [])

  // Remove file
  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => {
      const next = new Map(prev)
      const uploadFile = next.get(fileId)

      // Abort upload if in progress
      if (uploadFile?.upload) {
        uploadFile.upload.abort()
      }

      next.delete(fileId)
      return next
    })
  }, [])

  // Update file name
  const updateFileName = useCallback((fileId: string, newName: string) => {
    setFiles((prev) => {
      const next = new Map(prev)
      const file = next.get(fileId)
      if (file) {
        next.set(fileId, { ...file, name: newName })
      }
      return next
    })
  }, [])

  // Upload a single file - defined outside useCallback for direct calling
  const uploadFileById = (fileId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      setFiles((prev) => {
        const next = new Map(prev)
        const uploadFile = next.get(fileId)

        if (!uploadFile || uploadFile.status === 'error') {
          reject(new Error('File not found or has validation error'))
          return prev
        }

        // Get CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

        // Create Tus upload
        const upload = new tus.Upload(uploadFile.file, {
          endpoint: '/files',
          retryDelays: [0, 1000, 3000, 5000],
          chunkSize: 5 * 1024 * 1024, // 5 MB chunks
          headers: csrfToken ? {
            'X-CSRF-Token': csrfToken,
          } : {},
          metadata: {
            filename: uploadFile.name,
            filetype: uploadFile.type,
          },
          onError: (error) => {
            console.error('Upload error:', error)
            setFiles((prev) => {
              const next = new Map(prev)
              const file = next.get(fileId)
              if (file) {
                next.set(fileId, { ...file, status: 'error', error: error.message })
              }
              return next
            })
            reject(error)
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const percentage = ((bytesUploaded / bytesTotal) * 100)
            const currentTime = Date.now()

            setFiles((prev) => {
              const next = new Map(prev)
              const file = next.get(fileId)
              if (file) {
                let transferRate = file.transferRate || 0

                // Calculate transfer rate if we have previous data
                if (file.bytesUploaded !== undefined && file.lastUpdateTime !== undefined) {
                  const bytesChange = bytesUploaded - file.bytesUploaded
                  const timeChange = (currentTime - file.lastUpdateTime) / 1000 // Convert to seconds

                  if (timeChange > 0 && bytesChange > 0) {
                    // Calculate current rate
                    const currentRate = bytesChange / timeChange

                    // Apply smoothing (exponential moving average) to avoid jumpy values
                    transferRate = file.transferRate
                      ? file.transferRate * 0.7 + currentRate * 0.3
                      : currentRate
                  }
                }

                next.set(fileId, {
                  ...file,
                  progress: percentage,
                  status: 'uploading',
                  bytesUploaded,
                  lastUpdateTime: currentTime,
                  transferRate
                })
              }
              return next
            })
          },
          onSuccess: () => {
            const uploadURL = upload.url
            if (!uploadURL) {
              reject(new Error('No upload URL returned'))
              return
            }

            // Extract file ID from upload URL
            const tusFileId = uploadURL.split('/').pop()

            // Construct Shrine file data (Tus is configured as cache storage)
            const fileData = {
              id: tusFileId,
              storage: 'cache',
              metadata: {
                filename: uploadFile.name,
                size: uploadFile.size,
                mime_type: uploadFile.type,
              },
            }

            setFiles((prev) => {
              const next = new Map(prev)
              const file = next.get(fileId)
              if (file) {
                next.set(fileId, {
                  ...file,
                  status: 'success',
                  progress: 100,
                  uploadedData: JSON.stringify(fileData),
                })
              }
              return next
            })
            resolve()
          },
        })

        // Store upload instance and start
        next.set(fileId, { ...uploadFile, upload, status: 'uploading' })
        upload.start()

        return next
      })
    })
  }

  // Wrapped version for external use
  const uploadFile = useCallback((fileId: string) => {
    return uploadFileById(fileId)
  }, [])

  // Upload all pending files
  const uploadAll = useCallback(async () => {
    const pendingFiles = Array.from(files.values()).filter(
      (f) => f.status === 'pending'
    )

    const uploadPromises = pendingFiles.map((file) => uploadFile(file.id))

    try {
      await Promise.all(uploadPromises)
    } catch (error) {
      console.error('Some uploads failed:', error)
      // Don't throw - some files may have succeeded
    }
  }, [files, uploadFile])

  // Upload specific files by ID
  const uploadFiles = useCallback(async (fileIds: string[]) => {
    const uploadPromises = fileIds.map((fileId) => uploadFile(fileId))

    try {
      await Promise.all(uploadPromises)
    } catch (error) {
      console.error('Some uploads failed:', error)
      // Don't throw - some files may have succeeded
    }
  }, [uploadFile])

  // Clear all files
  const clearAll = useCallback(() => {
    // Abort all uploads
    files.forEach((file) => {
      if (file.upload) {
        file.upload.abort()
      }
    })
    setFiles(new Map())
  }, [files])

  // Memoize files array to prevent unnecessary re-renders
  const filesArray = useMemo(() => Array.from(files.values()), [files])

  return {
    files: filesArray,
    addFiles,
    removeFile,
    updateFileName,
    uploadFile,
    uploadAll,
    uploadFiles,
    clearAll,
  }
}
